import { useState, useEffect } from "react";
import { C } from "./re-colors.ts";

// ── FORMAT UTILITY ────────────────────────────────────────────────
export const fmt = (v: number) =>
  v >= 1e9 ? `$${(v / 1e9).toFixed(2)}B`
  : v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M`
  : v >= 1e3 ? `$${(v / 1e3).toFixed(0)}K`
  : `$${v}`;

// ── BADGE ─────────────────────────────────────────────────────────
export function Bdg({ label, color }: { label: string; color: string }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase",
      padding: "3px 9px", borderRadius: 20,
      background: `${color}18`, color, border: `1px solid ${color}30`,
    }}>
      {label}
    </span>
  );
}

// ── SPARKLINE ─────────────────────────────────────────────────────
export function Spark({ data, color = C.burgundy, h = 32 }: { data: number[]; color?: string; h?: number }) {
  const w = 80;
  const mn = Math.min(...data);
  const mx = Math.max(...data);
  const pts = data
    .map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - mn) / (mx - mn || 1)) * (h - 4) - 2}`)
    .join(" ");
  return (
    <svg width={w} height={h} style={{ overflow: "visible" }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
      <polyline points={`0,${h} ${pts} ${w},${h}`} fill={`${color}10`} stroke="none" />
    </svg>
  );
}

// ── PROGRESS BAR ──────────────────────────────────────────────────
export function Bar({ val, max = 100, color = C.burgundy }: { val: number; max?: number; color?: string }) {
  return (
    <div style={{ height: 4, background: C.border, borderRadius: 4 }}>
      <div style={{
        height: "100%", width: `${Math.min(100, (val / max) * 100)}%`,
        background: color, borderRadius: 4, transition: "width .4s",
      }} />
    </div>
  );
}

// ── STATUS DOT ────────────────────────────────────────────────────
export function SDot({ s }: { s: string }) {
  const clr = ({ critical: C.burgundy, high: C.gold, ok: C.forest, medium: C.rust } as any)[s] || C.muted;
  return (
    <span style={{
      display: "inline-block", width: 8, height: 8, borderRadius: "50%",
      background: clr, boxShadow: `0 0 5px ${clr}80`, flexShrink: 0,
    }} />
  );
}

// ── SOURCE PILL ───────────────────────────────────────────────────
export function SourcePill({ src }: { src: string }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 5, marginTop: 8,
      padding: "4px 8px", borderRadius: 6,
      background: C.surface, border: `1px solid ${C.border}`, width: "fit-content",
    }}>
      <span style={{ fontSize: 9, color: C.dim }}>🔗</span>
      <span style={{ fontSize: 9, color: C.dim, fontWeight: 600, letterSpacing: .3 }}>SOURCE:</span>
      <span style={{ fontSize: 9, color: C.muted }}>{src}</span>
    </div>
  );
}

// ── CARD ──────────────────────────────────────────────────────────
export function Card({
  children, onClick, style = {}, glow,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  style?: React.CSSProperties;
  glow?: string;
}) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => onClick && setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? C.cardHover : C.card,
        border: `1px solid ${hov && onClick ? C.borderLight : C.border}`,
        borderRadius: 12, padding: 18, transition: "all .18s",
        cursor: onClick ? "pointer" : "default",
        boxShadow: glow
          ? `0 0 0 1px ${glow}30,0 2px 16px ${glow}12`
          : hov && onClick
            ? "0 4px 20px rgba(42,31,24,.10)"
            : "0 1px 4px rgba(42,31,24,.04)",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ── KPI CARD ──────────────────────────────────────────────────────
export function KpiCard({
  label, value, sub, trend, spark, color, onClick,
}: {
  label: string; value: string | number; sub?: string;
  trend?: "up" | "dn"; spark?: number[]; color?: string; onClick?: () => void;
}) {
  const c = color || C.burgundy;
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => onClick && setHov(true)}
      onMouseLeave={() => setHov(false)}
      onClick={onClick}
      style={{
        background: hov ? C.cardHover : C.card,
        border: `1px solid ${hov && onClick ? C.borderLight : C.border}`,
        borderRadius: 12, padding: 18, flex: 1, minWidth: 120,
        cursor: onClick ? "pointer" : "default",
        boxShadow: hov && onClick ? "0 4px 20px rgba(42,31,24,.10)" : "0 1px 4px rgba(42,31,24,.04)",
        transition: "all .18s", position: "relative",
      }}
    >
      {onClick && hov && (
        <div style={{ position: "absolute", bottom: 8, right: 10, fontSize: 9, color: C.muted, fontWeight: 600 }}>
          View details →
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: .8, fontWeight: 700 }}>
          {label}
        </div>
        {spark && <Spark data={spark} color={c} />}
      </div>
      <div style={{ fontSize: 26, fontWeight: 800, color: C.text, margin: "8px 0 4px", letterSpacing: -.5 }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 11, fontWeight: 600, color: trend === "up" ? C.forest : trend === "dn" ? C.burgundy : C.muted }}>
          {trend === "up" ? "▲ " : trend === "dn" ? "▼ " : ""}{sub}
        </div>
      )}
    </div>
  );
}

// ── INSIGHT CARD ──────────────────────────────────────────────────
export function InsightCard({
  icon, title, body, bdg, bColor, priority, onClick,
}: {
  icon: string; title: string; body: string;
  bdg?: string; bColor?: string; priority?: string; onClick?: () => void;
}) {
  const bar = ({ critical: C.burgundy, high: C.gold, medium: C.rust, low: C.forest } as any)[priority ?? ""] || C.burgundy;
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? C.cardHover : C.card, border: `1px solid ${C.border}`,
        borderRadius: 12, overflow: "hidden", cursor: "pointer",
        boxShadow: hov ? "0 4px 20px rgba(42,31,24,.10)" : "0 1px 4px rgba(42,31,24,.04)",
        transition: "all .18s",
      }}
    >
      <div style={{ display: "flex" }}>
        <div style={{ width: 3, background: bar, flexShrink: 0, borderRadius: "12px 0 0 12px" }} />
        <div style={{ padding: "13px 15px", flex: 1 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 5 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <span style={{ fontSize: 15 }}>{icon}</span>
              <span style={{ fontWeight: 700, fontSize: 13, color: C.text, lineHeight: 1.3 }}>{title}</span>
            </div>
            {bdg && <Bdg label={bdg} color={bColor || C.burgundy} />}
          </div>
          <p style={{ margin: 0, fontSize: 12, color: C.muted, lineHeight: 1.6 }}>{body}</p>
          <div style={{ marginTop: 8, fontSize: 11, color: bar, fontWeight: 700 }}>View details →</div>
        </div>
      </div>
    </div>
  );
}

// ── KEY-VALUE LIST ────────────────────────────────────────────────
export function KVList({ rows }: { rows: (string | null | undefined)[][] }) {
  return (
    <div>
      {rows.map(([l, v, vc], i) => (
        <div key={i} style={{
          display: "flex", justifyContent: "space-between", padding: "9px 0",
          borderBottom: i < rows.length - 1 ? `1px solid ${C.border}` : "none",
        }}>
          <span style={{ fontSize: 12, color: C.muted }}>{l}</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: (vc as string) || C.text }}>{v}</span>
        </div>
      ))}
    </div>
  );
}

// ── AI INSIGHT BOX ────────────────────────────────────────────────
export function AiBox({ text, c = C.burgundy, source }: { text: string; c?: string; source?: string }) {
  return (
    <Card style={{ background: `${c}08`, border: `1px solid ${c}25`, padding: 14, marginTop: 0 }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: c, letterSpacing: .8, textTransform: "uppercase", marginBottom: 7 }}>
        AI Insight
      </div>
      <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.7, margin: 0 }}>{text}</p>
      {source && <SourcePill src={source} />}
    </Card>
  );
}

// ── PAGE HEADER ───────────────────────────────────────────────────
export function PH({
  title, sub, back, accentColor,
}: {
  title: string; sub?: string; back?: () => void; accentColor?: string;
}) {
  return (
    <div style={{ marginBottom: 22 }}>
      {back && (
        <button
          onClick={back}
          style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 12, padding: "0 0 10px", display: "flex", alignItems: "center", gap: 5 }}
        >
          ← Back
        </button>
      )}
      <h1 style={{ margin: 0, fontSize: 21, fontWeight: 800, color: C.text, letterSpacing: -.3 }}>{title}</h1>
      {sub && <p style={{ margin: "4px 0 0", fontSize: 13, color: C.muted }}>{sub}</p>}
      <div style={{ height: 2, background: `linear-gradient(90deg,${accentColor || C.burgundy},transparent)`, marginTop: 13, borderRadius: 2 }} />
    </div>
  );
}

// ── RESPONSIVE HOOK ───────────────────────────────────────────────
export function useIsMobile(bp = 720) {
  const [mobile, setMobile] = useState(() => window.innerWidth < bp);
  useEffect(() => {
    const h = () => setMobile(window.innerWidth < bp);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, [bp]);
  return mobile;
}

// ── TWO COLUMN LAYOUT ─────────────────────────────────────────────
export function TwoCol({
  left, right, ratio = "2fr 1fr",
}: {
  left: React.ReactNode; right: React.ReactNode; ratio?: string;
}) {
  const isMobile = useIsMobile();
  return (
    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : ratio, gap: 16 }}>
      {left}
      {right}
    </div>
  );
}
